import { useLazyQuery, useQuery } from "@apollo/client";
import moment from "moment";
import { FC, useEffect, useRef, useState } from "react";
import { Flex, Box, Text, Card } from 'rebass/styled-components'
import PleaseWait from "../../components/PleaseWait";
import { GET_PATIENT_BASELINE_SURVEY_ANSWERS, GET_PATIENT_FOLLOWUP_SURVEY_ANSWERS, GET_PATIENT_SURVEYS_AVERAGES, GET_PATIENT_SURVEYS_INFO } from "../../graphql/queries";
import { IGetBaselineSurveyAnswersProviderOutput, IGetBaselineSurveyAnswersProviderInput, IGetFollowUpSurveyAnswersProviderOutput, IGetFollowUpSurveyAnswersProviderInput, IGetSurveysInfoProviderInput, IGetSurveysInfoProviderOutput, ISurveyInfo, IGetSurveysAveragesOutput, IGetSurveysAveragesInput } from "../../graphql/types/queries/survey";
import { BASELINE_QUESTIONS } from "../surveys/questions/baselineQuestions";
import { SurveyChart } from "../surveys/SurveyChart";
import { getDefaultValueSingle } from "../surveys/utils";
import { SingelSurveyAnswers, SurveyAnswers } from "./components/SurveyAnswers";
import { ITab, Tabulator } from "./components/Tabulator";
import { useIsVisible } from "../../hooks/useIsVisible";

interface IProps {
  id: string,
  isMinimized?: boolean
}
interface IPropsSurveysInner {
  id: string,
  dataInfo: ISurveyInfo
  isMinimized?: boolean
  dataBaseline: IGetBaselineSurveyAnswersProviderOutput
  loadingBaseline: boolean
  dataFollowup: IGetFollowUpSurveyAnswersProviderOutput | undefined
  loadingFollowup: boolean
  dataSurveyCharts: IGetSurveysAveragesOutput | undefined
  loadingSurveyCharts: boolean
}
function getAvg(grades: Array<number>) {
  const total = grades.reduce((acc, c) => acc + c, 0);
  return total / grades.length;
}
const SurveysInner: FC<IPropsSurveysInner> = ({
  id,
  dataInfo,
  dataBaseline,
  loadingBaseline,
  dataFollowup,
  loadingFollowup,
  dataSurveyCharts,
  loadingSurveyCharts,
  isMinimized
}) => {
  const [chartData, setChartData] = useState<Array<{
    question: string,
    [key: string]: any
  }> | null>(null)
  const [filledSurveys, setFilledSurveys] = useState<Array<string>>([])

  useEffect(() => {
    if (loadingSurveyCharts) return
    if (!dataSurveyCharts || !dataSurveyCharts.providerSurveyAnswersProvider) return;
    let dta: Array<{
      question: string,
      [key: string]: any
    }> = [];

    setFilledSurveys(dataSurveyCharts.providerSurveyAnswersProvider.map(x => x.survey))
    for (let index = 0; index < dataSurveyCharts.providerSurveyAnswersProvider.length; index++) {
      const element = dataSurveyCharts.providerSurveyAnswersProvider[index];

      const dtaIndexAnxiety = dta.findIndex(x => x.question === 'Anxiety')
      if (dtaIndexAnxiety < 0) {
        dta.push({
          question: "Anxiety",
          [element.survey]: getAvg(element.avg.anxiety.values)
        })
      } else {
        dta[dtaIndexAnxiety][element.survey] = getAvg(element.avg.anxiety.values)
      }

      const dtaIndexCognitiveComplaints = dta.findIndex(x => x.question === 'Cognitive Complaints')
      if (dtaIndexCognitiveComplaints < 0) {
        dta.push({
          question: "Cognitive Complaints",
          [element.survey]: getAvg(element.avg.cognitiveComplaints.values)
        })
      } else {
        dta[dtaIndexCognitiveComplaints][element.survey] = getAvg(element.avg.cognitiveComplaints.values)
      }

      const dtaIndexcosmeticComplaints = dta.findIndex(x => x.question === 'Cosmetic Complaints')
      if (dtaIndexcosmeticComplaints < 0) {
        dta.push({
          question: "Cosmetic Complaints",
          [element.survey]: getAvg(element.avg.cosmeticComplaints.values)
        })
      } else {
        dta[dtaIndexcosmeticComplaints][element.survey] = getAvg(element.avg.cosmeticComplaints.values)
      }

      const dtaIndexDepressivity = dta.findIndex(x => x.question === 'Depressivity')
      if (dtaIndexDepressivity < 0) {
        dta.push({
          question: "Depressivity",
          [element.survey]: getAvg(element.avg.depressivity.values)
        })
      } else {
        dta[dtaIndexDepressivity][element.survey] = getAvg(element.avg.depressivity.values)
      }

      const dtaIndexhypothyroidSymptoms = dta.findIndex(x => x.question === 'Hypothyroid Symptoms')
      if (dtaIndexhypothyroidSymptoms < 0) {
        dta.push({
          question: "Hypothyroid Symptoms",
          [element.survey]: getAvg(element.avg.hypothyroidSymptoms.values)
        })
      } else {
        dta[dtaIndexhypothyroidSymptoms][element.survey] = getAvg(element.avg.hypothyroidSymptoms.values)
      }

      const dtaIndeximpairedDailyLife = dta.findIndex(x => x.question === 'Impaired Daily Life')
      if (dtaIndeximpairedDailyLife < 0) {
        dta.push({
          question: "Impaired Daily Life",
          [element.survey]: getAvg(element.avg.impairedDailyLife.values)
        })
      } else {
        dta[dtaIndeximpairedDailyLife][element.survey] = getAvg(element.avg.impairedDailyLife.values)
      }

      const dtaIndexoverallQoL = dta.findIndex(x => x.question === 'Quality of Life')
      if (dtaIndexoverallQoL < 0) {
        dta.push({
          question: "Quality of Life",
          [element.survey]: getAvg(element.avg.overallQoL.values)
        })
      } else {
        dta[dtaIndexoverallQoL][element.survey] = getAvg(element.avg.overallQoL.values)
      }

      const dtaIndexTiredness = dta.findIndex(x => x.question === 'Tiredness')
      if (dtaIndexTiredness < 0) {
        dta.push({
          question: "Tiredness",
          [element.survey]: getAvg(element.avg.tiredness.values)
        })
      } else {
        dta[dtaIndexTiredness][element.survey] = getAvg(element.avg.tiredness.values)
      }
    }


    setChartData(dta);
  }, [
    dataSurveyCharts,
    loadingSurveyCharts]);

  const getColors = (name: string) => {
    switch (name) {
      case 'Baseline':
        return "rgb(246, 162, 147,0.6)"
      case 'FollowUpSurvey1':
        return "rgb(78, 101, 138,0.6)"
      case 'FollowUpSurvey2':
        return "rgb(184, 214, 245,0.6)"
      case 'FollowUpSurvey3':
        return "rgb(253, 245, 242, 0.6)"
      case 'FollowUpSurvey4':
        return "rgb(174, 149, 139)"

      default:
        break;
    }
  }
  const ans = [{ surveyName: 'baseline', answers: (dataBaseline?.getBaselineSurveyAnswersProvider?.values || ''), lastUpdated: dataBaseline.getBaselineSurveyAnswersProvider.updatedAt }]
  for (let index = 0; index < (dataFollowup?.getFollowUpSurveyAnswersProvider || []).length; index++) {
    const element = (dataFollowup?.getFollowUpSurveyAnswersProvider || [])[index];
    if (!!(element.answers?.values || '')) {
      ans.push({
        surveyName: element.survey,
        lastUpdated: element.answers?.updatedAt,
        answers: element.answers?.values || ''
      })
    }
  }

  const tabsAnswers: Array<ITab> = []
  for (let index = 0; index < ans.length; index++) {
    const element = ans[index];
    console.log({ element })
    let sn = ''
    switch (element.surveyName.toLowerCase()) {
      case 'baseline':
        sn = 'Baseline'
        break;
      case 'followupsurvey1':
        sn = 'Followup 1'
        break;
      case 'followupsurvey2':
        sn = 'Followup 2'
        break;
      case 'followupsurvey3':
        sn = 'Followup 3'
        break;


      default:
        break;
    }
    tabsAnswers.push({
      title: sn,
      name: element.surveyName,
      icon: (<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M18 2H13C13 2.55 12.55 3 12 3C11.45 3 11 2.55 11 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V4C20 2.9 19.1 2 18 2Z" fill="#455A64" />
        <path d="M18 20.5H6C5.7 20.5 5.5 20.3 5.5 20V4C5.5 3.7 5.7 3.5 6 3.5H18C18.3 3.5 18.5 3.7 18.5 4V20C18.5 20.3 18.3 20.5 18 20.5Z" fill="white" />
        <path d="M13 2C13 2.55 12.55 3 12 3C11.45 3 11 2.55 11 2H7.5V4C7.5 4.55 7.95 5 8.5 5H15.5C16.05 5 16.5 4.55 16.5 4V2H13Z" fill="#90A4AE" />
        <path d="M12 0C10.9 0 10 0.9 10 2C10 3.1 10.9 4 12 4C13.1 4 14 3.1 14 2C14 0.9 13.1 0 12 0ZM12 3C11.45 3 11 2.55 11 2C11 1.45 11.45 1 12 1C12.55 1 13 1.45 13 2C13 2.55 12.55 3 12 3Z" fill="#90A4AE" />
        <path d="M10.5 10H16.5V11H10.5V10ZM7.5 9.5H9.5V11.5H7.5V9.5Z" fill="#CFD8DC" />
        <path d="M10.5 14.5H16.5V15.5H10.5V14.5ZM7.5 14H9.5V16H7.5V14Z" fill="#03A9F4" />
      </svg>
      ),
      content: (
        <SingelSurveyAnswers answers={element.answers} lastUpdate={element.lastUpdated?.toString()} surveyName={element.surveyName} key={element.surveyName} />
      )
    })
  }

  return (
    <Tabulator isMinimized={isMinimized} tabs={[{
      title: 'Overview',
      name: 'Overview',
      icon: (<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M21.7219 9.30625L12.4406 2.55625C12.3128 2.4626 12.1584 2.41211 12 2.41211C11.8415 2.41211 11.6872 2.4626 11.5594 2.55625L2.27811 9.30625C2.14994 9.39943 2.05459 9.53083 2.00575 9.68157C1.9569 9.83232 1.95707 9.99467 2.00624 10.1453L5.55233 21.0672C5.60128 21.2176 5.69656 21.3488 5.82453 21.4418C5.9525 21.5348 6.10662 21.585 6.26483 21.5852H17.7375C18.0633 21.5852 18.3492 21.3766 18.45 21.0672L21.9961 10.1453C22.0945 9.83594 21.9844 9.49844 21.7219 9.30625ZM19.6547 9.77266L17.3461 10.5227L12.6586 7.13125V4.68437L19.6547 9.77266ZM10.9219 13.293L8.83358 16.1594L7.53983 12.1937L10.9219 13.293ZM8.19608 11.0242L11.3437 8.74844V12.0484L8.19608 11.0242ZM12 14.043L14.2008 17.0641H9.79921L12 14.043ZM12.6586 8.74844L15.8039 11.0242L12.6586 12.0461V8.74844ZM13.0781 13.293L16.4601 12.1937L15.1664 16.1594L13.0781 13.293ZM11.3437 4.68437V7.13125L6.6539 10.5227L4.3453 9.77266L11.3437 4.68437ZM3.96796 11.0336L5.9953 11.6922L7.87968 17.4672L6.61874 19.1992L3.96796 11.0336ZM7.6664 19.9914L8.8453 18.3742H15.157L16.3359 19.9914H7.6664ZM17.3812 19.1992L16.1203 17.4672L18.0047 11.6922L20.032 11.0336L17.3812 19.1992Z" fill="#344F7A" />
      </svg>
      ),
      content: (
        <Flex flexDirection={'column'} width='100%' padding={'20px'}>
          <Flex flexDirection={isMinimized ? 'column-reverse' : 'row'}>
            <Flex flexDirection={'column'} flex='1'>
              <Text fontWeight={600} fontSize='16px' mb={2}>Overview</Text>
              <Text fontWeight={300} fontSize='16px' mb={1}>(0: Not at all, 1: A bit, 2: Somewhat, 3: Quite a lot, 4: Fully)</Text>
              <SurveyChart chartData={chartData} hideLegend filledSurveys={filledSurveys} />
            </Flex>
            <Flex>
              <Flex flexDirection={'column'} flex='1'>
                <Text fontWeight={600} fontSize='16px' mb={4}>Legend</Text>
                {JSON.parse(JSON.stringify(dataInfo.statuses || []))?.map((x: any, i: number) => {
                  return (
                    <Flex mb={3}>
                      <Box height={'24px'} width={'24px'} backgroundColor={getColors(x.name)} style={{ border: getColors(x.name) }} mr={3} />
                      <Flex flexDirection={'column'}>
                        <Text>{x.name}</Text>
                        <Text fontSize={'12px'} fontWeight={300}>{x.status || 'not started'}</Text>
                        {x.triggerOn ? (
                          <Text fontSize={'12px'} fontWeight={300}>trigger on: {moment(x.triggerOn).format('MM/DD/YYYY')}</Text>
                        ) : x.lastUpdatedOn ?
                          (<Text fontSize={'12px'} fontWeight={300}>Last updated on: {moment(x.lastUpdatedOn).format('MM/DD/YYYY')}</Text>)
                          : ''
                        }
                      </Flex>
                    </Flex>
                  )
                })}
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      )
    },
    ...tabsAnswers

    ]} />


    // <Flex flexDirection={'column'} width='100%'>
    //   <Flex flexDirection={'row'}>
    //     <Card style={{ width: '100%', borderRadius: '30px', padding: '20px', flex: 1 }} mb={4}>

    //     </Card>
    //   </Flex>



    //   {!loadingBaseline && !loadingFollowup && (
    //     <SurveyAnswers answers={ans} />
    //   )}

    // </Flex>
  )
}



export const Surveys: FC<IProps> = ({ id, isMinimized }) => {

  const ref = useRef<any>();
  const isVisible = useIsVisible(ref);

  const [load, { data, loading }] = useLazyQuery<IGetSurveysInfoProviderOutput, IGetSurveysInfoProviderInput>(GET_PATIENT_SURVEYS_INFO, {
    variables: {
      id: id
    }
  })
  const [loadBaseline, { data: dataBaseline, loading: loadingBaseline }] = useLazyQuery<IGetBaselineSurveyAnswersProviderOutput, IGetBaselineSurveyAnswersProviderInput>(GET_PATIENT_BASELINE_SURVEY_ANSWERS, {
    variables: {
      id: id
    }
  })
  const [loadFollowUp, { data: dataFollowup, loading: loadingFollowup }] = useLazyQuery<IGetFollowUpSurveyAnswersProviderOutput, IGetFollowUpSurveyAnswersProviderInput>(GET_PATIENT_FOLLOWUP_SURVEY_ANSWERS, {
    variables: {
      id: id,
      surveyName: 'FollowUpSurvey1'
    }
  })
  const [loadCharts, { data: dataSurveyCharts, loading: loadingSurveyCharts }] = useLazyQuery<IGetSurveysAveragesOutput, IGetSurveysAveragesInput>(GET_PATIENT_SURVEYS_AVERAGES, {
    variables: {
      id: id,
    }
  })

  useEffect(() => {
    if (!!isVisible) {
      load()
      loadBaseline()
      loadFollowUp()
      loadCharts()
    }
  }, [isVisible])

  return (
    <Flex ref={ref} flexDirection={'column'} width='100%'>
      {((!!loading) || (!data || !data.getSurveysInfoProvider) || (!!data.getSurveysInfoProvider && data.getSurveysInfoProvider.mustCompleteBaselineSurvey === null) ||
        (!!loadingBaseline || loadingFollowup || loadingSurveyCharts) ||
        (!dataBaseline || !dataBaseline.getBaselineSurveyAnswersProvider || !dataBaseline.getBaselineSurveyAnswersProvider.status)) ? (

        <Tabulator isMinimized={isMinimized} tabs={[{
          title: 'Surveys',
          name: 'Surveys',
          icon: (<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 2H13C13 2.55 12.55 3 12 3C11.45 3 11 2.55 11 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V4C20 2.9 19.1 2 18 2Z" fill="#455A64" />
            <path d="M18 20.5H6C5.7 20.5 5.5 20.3 5.5 20V4C5.5 3.7 5.7 3.5 6 3.5H18C18.3 3.5 18.5 3.7 18.5 4V20C18.5 20.3 18.3 20.5 18 20.5Z" fill="white" />
            <path d="M13 2C13 2.55 12.55 3 12 3C11.45 3 11 2.55 11 2H7.5V4C7.5 4.55 7.95 5 8.5 5H15.5C16.05 5 16.5 4.55 16.5 4V2H13Z" fill="#90A4AE" />
            <path d="M12 0C10.9 0 10 0.9 10 2C10 3.1 10.9 4 12 4C13.1 4 14 3.1 14 2C14 0.9 13.1 0 12 0ZM12 3C11.45 3 11 2.55 11 2C11 1.45 11.45 1 12 1C12.55 1 13 1.45 13 2C13 2.55 12.55 3 12 3Z" fill="#90A4AE" />
            <path d="M10.5 10H16.5V11H10.5V10ZM7.5 9.5H9.5V11.5H7.5V9.5Z" fill="#CFD8DC" />
            <path d="M10.5 14.5H16.5V15.5H10.5V14.5ZM7.5 14H9.5V16H7.5V14Z" fill="#03A9F4" />
          </svg>
          ),
          content: (
            <>
              {!!loading ? (
                <Flex flex={1} flexDirection={'column'} justifyContent='center' alignItems={'center'} >
                  <PleaseWait />
                </Flex>
              ) : (
                <>
                  {!data || !data.getSurveysInfoProvider ? (
                    <Flex flexDirection={'column'} width='100%' justifyContent={'center'} alignItems={'center'}>
                      <Text textAlign={'center'} fontSize='24px' fontWeight={300} marginTop='42px' opacity={0.55}>Patient didn't complete baseline survey</Text>
                    </Flex>
                  ) : (
                    <>
                      {!!data.getSurveysInfoProvider && data.getSurveysInfoProvider.mustCompleteBaselineSurvey === null ? (
                        <Flex flexDirection={'column'} width='100%' justifyContent={'center'} alignItems={'center'}>
                          <Text textAlign={'center'} fontSize='24px' fontWeight={300} marginTop='42px' opacity={0.55}>Patient became member prior to baseline survey feature.<br /><br />The patient won't fill any health surveys</Text>
                        </Flex>
                      ) : (
                        <>
                          {!!loadingBaseline || loadingFollowup || loadingSurveyCharts ? (
                            <Flex flex={1} flexDirection={'column'} justifyContent='center' alignItems={'center'} >
                              <PleaseWait />
                            </Flex>
                          ) : (
                            <>
                              {!dataBaseline || !dataBaseline.getBaselineSurveyAnswersProvider || !dataBaseline.getBaselineSurveyAnswersProvider.status ? (
                                <Flex flexDirection={'column'} width='100%' justifyContent={'center'} alignItems={'center'}>
                                  <Text textAlign={'center'} fontSize='24px' fontWeight={300} marginTop='42px' opacity={0.55}>Patient has not started the baseline survey</Text>
                                </Flex>
                              ) : null}
                            </>
                          )}
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )
        }]} />
      ) : (
        <SurveysInner
          isMinimized={isMinimized}
          id={id}
          dataInfo={data.getSurveysInfoProvider}
          dataBaseline={dataBaseline}
          loadingBaseline={loadingBaseline}
          dataFollowup={dataFollowup}
          loadingFollowup={loadingFollowup}
          dataSurveyCharts={dataSurveyCharts}
          loadingSurveyCharts={loadingSurveyCharts}
        />
      )}
    </Flex>
  )
}